import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

interface PatientMergeSide {
  uuid: string;
  primaryClientId: string | null;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface PatientMergeMove {
  kind: string;
  repoint: number;
}

/** Mirrors brian's `PatientMergePlanOut`, returned by both preview and apply. */
export interface PatientMergePlan {
  keep: PatientMergeSide;
  absorb: PatientMergeSide;
  moves: PatientMergeMove[];
  visits: number;
  ownerAdopted: boolean;
  filledColumns: string[];
  warnings: string[];
}

const toPatientMergeSide = (
  p: components["schemas"]["PatientOut"],
): PatientMergeSide => ({
  uuid: p.uuid,
  primaryClientId: p.primary_client_uuid,
  name: p.name,
  createdAt: p.created_at,
  updatedAt: p.updated_at,
});

const toPatientMergePlan = (
  plan: components["schemas"]["PatientMergePlanOut"],
): PatientMergePlan => ({
  keep: toPatientMergeSide(plan.keep),
  absorb: toPatientMergeSide(plan.absorb),
  moves: plan.moves.map((m) => ({
    kind: m.kind,
    repoint: m.repoint,
  })),
  visits: plan.visits,
  ownerAdopted: plan.owner_adopted,
  filledColumns: plan.filled_columns,
  warnings: plan.warnings,
});

interface PatientMergeClient {
  getPlan: (keepUuid: string, absorbUuid: string) => Promise<PatientMergePlan>;
  apply: (keepUuid: string, absorbUuid: string) => Promise<PatientMergePlan>;
}

export const patientMergeClient: PatientMergeClient = {
  getPlan: (keepUuid, absorbUuid) =>
    request(() =>
      apiClient.GET("/patients/{patient_uuid}/merge-plan", {
        params: {
          path: { patient_uuid: keepUuid },
          query: { absorb: absorbUuid },
        },
      }),
    ).then(toPatientMergePlan),

  // Irreversible: deletes the absorbed patient record.
  apply: (keepUuid, absorbUuid) =>
    request(() =>
      apiClient.POST("/patients/{patient_uuid}/merge", {
        params: { path: { patient_uuid: keepUuid } },
        body: { absorb_uuid: absorbUuid },
      }),
    ).then(toPatientMergePlan),
};
